import { IdentifierValue, LiteralType, QueryExprKind, QueryExprSyntax } from 'src/app/services/api.services';
import { lazy, Lazy } from 'src/app/shared/utils';

export class TextLocation {
  constructor(
    public readonly startLine: number,
    public readonly startColumn: number,
    public readonly endLine: number,
    public readonly endColumn: number) { }

  get startLineIndex() { return this.startLine - 1; }
  get startColumnIndex() { return this.startColumn - 1; }
  get endLineIndex() { return this.endLine - 1; }
  get endColumnIndex() { return this.endColumn - 1; }

  get singleline() { return this.startLine === this.endLine; }

  equals(other: TextLocation) {
    return this.startLine == other.startLine
      && this.startColumn == other.startColumn
      && this.endLine == other.endLine
      && this.endColumn == other.endColumn;
  }

  toString() {
    if (this.startLine == this.endLine) {
      if (this.startColumn == this.endColumn) {
        return `${this.startLine}:${this.startColumn}`;
      }
      else {
        return `${this.startLine}:${this.startColumn}-${this.endColumn}`;
      }
    }
    else {
      return `${this.startLine}:${this.startColumn}-${this.endLine}:${this.endColumn}`;
    }
  }
}

export class SyntaxSpan extends TextLocation {
  static readonly empty = new SyntaxSpan([''], 0, 0, 0, 0);

  constructor(
    public readonly sourceLines: ReadonlyArray<string>,
    public readonly startLine: number,
    public readonly startColumn: number,
    public readonly endLine: number,
    public readonly endColumn: number) {
    super(startLine, startColumn, endLine, endColumn);
  }

  toString() {
    let sourceLines = this.sourceLines;

    if (this.singleline)
      return sourceLines[this.startLineIndex].substring(this.startColumnIndex, this.endColumnIndex);

    let buffer: string[] = [];

    buffer.push(sourceLines[this.startLineIndex].substring(this.startColumnIndex));

    for (let l = this.startLineIndex + 1; l < this.endLineIndex; ++l) {
      buffer.push(sourceLines[l]);
    }

    buffer.push(sourceLines[this.endLineIndex].substring(this.startColumnIndex, this.endColumnIndex));

    return buffer.join('\n');
  }
}

export import SyntaxKind = QueryExprKind;

export const SYNTAX_NODE_SYMBOL = Symbol('@@SyntaxNode');

export abstract class SyntaxNode implements QueryExprSyntax {
  constructor(
    public readonly kind: SyntaxKind,
    public readonly span: SyntaxSpan) {
  }

  get location(): TextLocation { return this.span; }

  equals(other: SyntaxNode): boolean {
    return this.location == other.location && this.sameSyntax(other);
  }

  sameSyntax(other: SyntaxNode): boolean {
    return this.kind == other.kind;
  }

  toString() {
    let buffer: string[] = [];
    new ToStringSyntaxVisitor(buffer).visit(this);
    return buffer.join('');
  }

  get debugString() {
    return this.toString();
  }

  static from(syntax: QueryExprSyntax | SyntaxNode): SyntaxNode {
    if (syntax instanceof SyntaxNode)
      return syntax;
    
    
    if (typeof syntax !== 'object' || !('kind' in syntax) || !Object.values(SyntaxKind).includes(syntax.kind))
      throw new Error(`Expected a SyntaxNode or an object { kind: SyntaxKind }`, { cause: syntax });

    if (Object.hasOwn(syntax, SYNTAX_NODE_SYMBOL)) 
      return syntax[SYNTAX_NODE_SYMBOL];
    
    let node = SyntaxNode.fromNodeObject(syntax);
    
    if (!Object.isFrozen(syntax)) {
      (syntax as any)[SYNTAX_NODE_SYMBOL] = node;
      Object.freeze(syntax);
    }

    return node;
  }

  private static fromNodeObject(syntax: QueryExprSyntax): SyntaxNode {
    if (syntax.kind == QueryExprKind.Binary) {
      return new BinarySyntax(SyntaxSpan.empty,
        (syntax as any).operator,
        SyntaxNode.from((syntax as any).left),
        SyntaxNode.from((syntax as any).right));
    }
    
    if (syntax.kind == QueryExprKind.FunctionCall) {
      return new FunctionCallSyntax(SyntaxSpan.empty,
        (syntax as any).functionName,
        (syntax as any).args.map(x => SyntaxNode.from(x)));
    }
    
    if (syntax.kind == QueryExprKind.Identifier) {
      return new IdentifierSyntax(SyntaxSpan.empty,
        (syntax as any).value);
    }
    
    if (syntax.kind == QueryExprKind.Index) {
      return new IndexSyntax(SyntaxSpan.empty,
        SyntaxNode.from((syntax as any).value),
        SyntaxNode.from((syntax as any).key));
    }
    
    if (syntax.kind == QueryExprKind.List) {
      return new ListSyntax(SyntaxSpan.empty,
        (syntax as any).items.map(x => SyntaxNode.from(x)));
    }
    
    if (syntax.kind == QueryExprKind.Literal) {
      return new LiteralSyntax(SyntaxSpan.empty,
        (syntax as any).type,
        (syntax as any).value);
    }
    
    if (syntax.kind == QueryExprKind.Map) {
      return new MapSyntax(SyntaxSpan.empty,
        (syntax as any).value.map(x => new MapSyntaxItem(<IdentifierSyntax>SyntaxNode.from(x.key), SyntaxNode.from(x.value))));
    }
    
    if (syntax.kind == QueryExprKind.Member) {
      return new MemberSyntax(SyntaxSpan.empty,
        SyntaxNode.from((syntax as any).subject),
        (syntax as any).key);
    }
    
    if (syntax.kind == QueryExprKind.Unary) {
      return new UnarySyntax(SyntaxSpan.empty,
        (syntax as any).operator,
        SyntaxNode.from((syntax as any).right));
    }

    throw new Error(`Invalid syntax kind '${syntax.kind}'`, { cause: syntax });
  }
}

export enum UnaryOperator {
  Plus = 'plus',
  Minus = 'minus',
  Not = 'not'
}

export class UnarySyntax extends SyntaxNode {
  constructor(
    span: SyntaxSpan,
    public readonly operator: UnaryOperator,
    public readonly right: SyntaxNode) {
    throwWhenNotSyntaxNode(right);
    super(SyntaxKind.Unary, span);
  }

  sameSyntax(other: UnarySyntax): boolean {
    return super.sameSyntax(other)
      && this.operator == other.operator
      && this.right.sameSyntax(other.right);
  }
}

export enum BinaryOperator {
  RaiseTo = 'pow',
  Multiply = 'mul',
  Divide = 'div',
  Mod = 'mod',
  Add = 'add',
  Subtract = 'sub',
  LessThan = 'lt',
  GreaterThan = 'gt',
  LessThanOrEqual = 'lte',
  GreaterThanOrEqual = 'gte',
  Equal = 'eq',
  NotEqual = 'neq',
  And = 'and',
  Or = 'or',
  Concat = 'cat',
  With = 'with',
  Without = 'without',
  In = 'in',
}

export class BinarySyntax extends SyntaxNode {
  constructor(
    span: SyntaxSpan,
    public readonly operator: BinaryOperator,
    public readonly left: SyntaxNode,
    public readonly right: SyntaxNode) {
    throwWhenNotSyntaxNode(left);
    throwWhenNotSyntaxNode(right);
    super(SyntaxKind.Binary, span);
  }

  sameSyntax(other: BinarySyntax): boolean {
    return this === other || (
      super.sameSyntax(other)
      && this.operator == other.operator
      && this.left.sameSyntax(other.left)
      && this.right.sameSyntax(other.right));
  }
}

export class FunctionCallSyntax extends SyntaxNode {
  constructor(
    span: SyntaxSpan,
    public readonly functionName: string,
    public readonly args: SyntaxNode[]) {
    throwWhenNotSyntaxNodes(args);
    super(SyntaxKind.FunctionCall, span);
  }

  sameSyntax(other: FunctionCallSyntax): boolean {
    return this === other || (
      super.sameSyntax(other)
      && this.functionName == other.functionName
      && this.args.length === other.args.length
      && !this.args.find((a, i) => !a.sameSyntax(other.args[i])));
  }
}

export class IdentifierSyntax extends SyntaxNode {
  public readonly value: string;

  constructor(
    span: SyntaxSpan,
    value: string | IdentifierValue) {
    super(SyntaxKind.Identifier, span);
    this.value = (
      typeof value === 'string' 
        ? value
        : value.value).toLowerCase();
  }

  sameSyntax(other: IdentifierSyntax): boolean {
    return this === other || (
      super.sameSyntax(other)
      && this.value == other.value);
  }
}

export class ListSyntax extends SyntaxNode {
  constructor(
    span: SyntaxSpan,
    public readonly items: SyntaxNode[]) {
    throwWhenNotSyntaxNodes(items);
    super(SyntaxKind.List, span);
  }

  sameSyntax(other: ListSyntax): boolean {
    return this === other || (
      super.sameSyntax(other)
      && this.items.length === other.items.length
      && !this.items.find((a, i) => !a.sameSyntax(other.items[i])));
  }
}

export class MapSyntax extends SyntaxNode {
  constructor(
    span: SyntaxSpan,
    public readonly items: MapSyntaxItem[]) {
    if (items.some(x => !(x instanceof MapSyntaxItem)))
      throw new Error(`Expected MapSyntaxItem`, { cause: items });

    super(SyntaxKind.Map, span);
  }

  sameSyntax(other: MapSyntax): boolean {
    return this === other || (
      super.sameSyntax(other)
      && this.items.length === other.items.length
      && !this.items.find((a, i) => !a.sameSyntax(other.items[i])));
  }
}

export class MapSyntaxItem {
  constructor(
    public readonly key: IdentifierSyntax,
    public readonly value: SyntaxNode) { 
    throwWhenNotSyntaxNode(key);
    throwWhenNotSyntaxNode(value);
  }

  sameSyntax(other: MapSyntaxItem): boolean {
    return this === other || (
      this.key.sameSyntax(other.key)
      && this.value.sameSyntax(other.value));
  }
}

export class LiteralSyntax extends SyntaxNode {
  constructor(
    span: SyntaxSpan,
    public readonly type: LiteralType,
    public readonly value: any) {
    super(SyntaxKind.Literal, span);
  }

  sameSyntax(other: LiteralSyntax): boolean {
    return this === other || (
      super.sameSyntax(other)
      && this.type == other.type
      && this.value === other.value);
  }
}

export class MemberSyntax extends SyntaxNode {
  public readonly key: string;

  constructor(
    span: SyntaxSpan,
    public readonly subject: SyntaxNode,
    key: string) {
    throwWhenNotSyntaxNode(subject);
    super(SyntaxKind.Member, span);
    this.key = key.toLowerCase();
  }

  sameSyntax(other: MemberSyntax): boolean {
    return this === other || (
      super.sameSyntax(other)
      && this.subject.sameSyntax(other.subject)
      && this.key == other.key);
  }
}

export class IndexSyntax extends SyntaxNode {
  constructor(
    span: SyntaxSpan,
    public readonly subject: SyntaxNode,
    public readonly key: SyntaxNode) {
    throwWhenNotSyntaxNode(subject);
    throwWhenNotSyntaxNode(key);
    super(SyntaxKind.Index, span);
  }

  sameSyntax(other: IndexSyntax): boolean {
    return this === other || (
      super.sameSyntax(other)
      && this.subject.sameSyntax(other.subject)
      && this.key.sameSyntax(other.key));
  }
}

export abstract class SyntaxVisitor<R = void, P = undefined> {
  visit(syntax: SyntaxNode, param?: P): R {
    if (syntax instanceof LiteralSyntax)
      return this.visitLiteral(syntax, param);

    if (syntax instanceof UnarySyntax)
      return this.visitUnary(syntax, param);

    if (syntax instanceof BinarySyntax)
      return this.visitBinary(syntax, param);

    if (syntax instanceof FunctionCallSyntax)
      return this.visitFunctionCall(syntax, param);

    if (syntax instanceof IdentifierSyntax)
      return this.visitIdentifier(syntax, param);
      
    if (syntax instanceof ListSyntax)
      return this.visitList(syntax, param);
      
    if (syntax instanceof MapSyntax)
      return this.visitMap(syntax, param);
      
    if (syntax instanceof IndexSyntax)
      return this.visitIndex(syntax, param);

    if (syntax instanceof MemberSyntax)
      return this.visitMember(syntax, param);

    throw new Error(`syntax is not a valid QueryExprSyntax`);
  }


  visitUnary(syntax: UnarySyntax, param?: P): R {
    this.visit(syntax.right, param);
    return;
  }

  visitBinary(syntax: BinarySyntax, param?: P): R {
    this.visit(syntax.left, param);
    this.visit(syntax.right, param);
    return;
  }


  visitIndex(syntax: IndexSyntax, param?: P): R {
    this.visit(syntax.subject, param);
    this.visit(syntax.key, param);
    return;
  }

  visitMember(syntax: MemberSyntax, param?: P): R {
    this.visit(syntax.subject, param);
    return;
  }


  visitFunctionCall(syntax: FunctionCallSyntax, param?: P): R {
    for (let arg of syntax.args)
      this.visit(arg, param);

    return;
  }

  visitIdentifier(syntax: IdentifierSyntax, param?: P): R {
    return;
  }

  visitList(syntax: ListSyntax, param?: P): R {
    for (let item of syntax.items)
      this.visit(item, param);
    return;
  }

  visitMap(syntax: MapSyntax, param?: P): R {
    for (let item of syntax.items) {
      this.visit(item.key, param);
      this.visit(item.value, param);
    }
    return;
  }

  visitLiteral(syntax: LiteralSyntax, param?: P): R {
    return;
  }
}

class ToStringSyntaxVisitor extends SyntaxVisitor {
  constructor(public buffer: string[]) {
    super();
  }

  visitUnary(syntax: UnarySyntax) {
    this.buffer.push('(');

    switch (syntax.operator) {
      case UnaryOperator.Plus:
        this.buffer.push('+');
        break;

      case UnaryOperator.Minus:
        this.buffer.push('-');
        break;

      case UnaryOperator.Not:
        this.buffer.push('NOT ');
        break;
    }

    this.visit(syntax.right);

    this.buffer.push(')');
  }

  visitBinary(syntax: BinarySyntax) {
    this.buffer.push('(');
    this.visit(syntax.left);


    this.buffer.push(' ');

    switch (syntax.operator) {
      case BinaryOperator.RaiseTo:
        this.buffer.push('^');
        break;

      case BinaryOperator.Multiply:
        this.buffer.push('*');
        break;

      case BinaryOperator.Divide:
        this.buffer.push('/');
        break;

      case BinaryOperator.Mod:
        this.buffer.push('%');
        break;

      case BinaryOperator.Add:
        this.buffer.push('+');
        break;

      case BinaryOperator.Subtract:
        this.buffer.push('-');
        break;

      case BinaryOperator.GreaterThan:
        this.buffer.push('>');
        break;

      case BinaryOperator.GreaterThanOrEqual:
        this.buffer.push('>=');
        break;

      case BinaryOperator.LessThan:
        this.buffer.push('<');
        break;

      case BinaryOperator.LessThanOrEqual:
        this.buffer.push('<=');
        break;

      case BinaryOperator.NotEqual:
        this.buffer.push('<>');
        break;

      case BinaryOperator.Equal:
        this.buffer.push('==');
        break;

      case BinaryOperator.And:
        this.buffer.push('AND');
        break;

      case BinaryOperator.Or:
        this.buffer.push('OR');
        break;

      case BinaryOperator.Concat:
        this.buffer.push('&');
        break;

      case BinaryOperator.With:
        this.buffer.push('WITH');
        break;

      case BinaryOperator.Without:
        this.buffer.push('WITHOUT');
        break;

      case BinaryOperator.In:
        this.buffer.push('IN');
        break;
    }

    this.buffer.push(' ');

    this.visit(syntax.right);

    this.buffer.push(')');
  }

  visitFunctionCall(syntax: FunctionCallSyntax) {
    this.buffer.push(syntax.functionName);
    this.buffer.push('(');
    this.buffer.push(...syntax.args.map(a => this.visit(a)).join(', '));
    this.buffer.push(')');
  }

  visitIdentifier(syntax: IdentifierSyntax) {
    this.buffer.push('[');
    this.buffer.push(syntax.value);
    this.buffer.push(']');
  }


  visitIndex(syntax: IndexSyntax) {
    this.visit(syntax.subject);
    this.buffer.push('[');
    this.visit(syntax.key);
    this.buffer.push(']');
  }

  visitMember(syntax: MemberSyntax) {
    this.visit(syntax.subject);
    this.buffer.push('.');
    this.buffer.push('[');
    this.buffer.push(syntax.key);
    this.buffer.push(']');
  }


  visitList(syntax: ListSyntax) {
    this.buffer.push('{');
    this.buffer.push(...syntax.items.map(i => this.visit(i)).join(', '));
    this.buffer.push('}');
  }

  visitMap(syntax: MapSyntax) {
    this.buffer.push('{');
    this.buffer.push(...syntax.items.map(i => [this.visit(i.key), this.visit(i.value)].join(': ')).join(', '));
    this.buffer.push('}');
  }

  visitLiteral(syntax: LiteralSyntax) {
    if (syntax.value === null || syntax.value === undefined) {
      this.buffer.push('NULL');
    }
    else if (syntax.value === true) {
      this.buffer.push('TRUE');
    }
    else if (syntax.value === false) {
      this.buffer.push('FALSE');
    }
    else if (typeof syntax.value === 'number') {
      this.buffer.push('' + syntax.value);
    }
    else if (typeof syntax.value === 'string') {
      this.buffer.push('"', syntax.value.replace(/\"/g, '""'), '"');
    }
    else if (typeof syntax.value === 'object' && 'toSyntaxString' in syntax.value) {
      this.buffer.push(syntax.value.toSyntaxString());
    }
    else {
      this.buffer.push('' + syntax.value);
    }
  }
}

function throwWhenNotSyntaxNode(x: any) {
  if (!(x instanceof SyntaxNode))
    throw new Error(`Expected SyntaxNode`, { cause: x });
}

function throwWhenNotSyntaxNodes(x: any[]) {
  x.forEach(throwWhenNotSyntaxNode);
}
